import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { gsap } from 'gsap';

// ===================================
// SCENE SETUP
// ===================================

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(
    75,
    window.innerWidth / window.innerHeight,
    0.1,
    1000
);
camera.position.z = 50;

const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true
});
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
document.getElementById('threejs-container').appendChild(renderer.domElement);

const gltfLoader = new GLTFLoader();

// ===================================
// PARTICLES - Spring Theme
// ===================================

// Green particles (leaves)
const particlesGeometry = new THREE.BufferGeometry();
const particlesCount = 800;
const posArray = new Float32Array(particlesCount * 3);

for (let i = 0; i < particlesCount * 3; i++) {
    posArray[i] = (Math.random() - 0.5) * 100;
}

particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));

const particlesMaterial = new THREE.PointsMaterial({
    size: 0.2,
    color: 0x4ade80, // Spring green
    transparent: true,
    opacity: 0.7,
    blending: THREE.AdditiveBlending
});

const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
scene.add(particlesMesh);

// Pink particles (cherry blossoms)
const pinkParticlesGeometry = new THREE.BufferGeometry();
const pinkParticlesCount = 400;
const pinkPosArray = new Float32Array(pinkParticlesCount * 3);

for (let i = 0; i < pinkParticlesCount * 3; i++) {
    pinkPosArray[i] = (Math.random() - 0.5) * 100;
}

pinkParticlesGeometry.setAttribute('position', new THREE.BufferAttribute(pinkPosArray, 3));

const pinkParticlesMaterial = new THREE.PointsMaterial({
    size: 0.25,
    color: 0xfda4af, // Cherry blossom pink
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending
});

const pinkParticlesMesh = new THREE.Points(pinkParticlesGeometry, pinkParticlesMaterial);
scene.add(pinkParticlesMesh);

// ===================================
// 3D LEAF MODELS - Spring Theme
// ===================================

const shapes = [];
let leafModel = null;

// Leaf configurations: [position, scale, color, wireframe]
// pos[0] is a fraction of the visible half-width (-1 = left edge, 1 = right edge),
// so leaves stay on the sides of the hero text at any window size
const leafConfigs = [
    { pos: [-0.72, 20, -90], scale: 0.15, color: 0x22c55e, wireframe: true, opacity: 0.4 },
    { pos: [0.75, -15, -95], scale: 0.18, color: 0xfda4af, wireframe: false, opacity: 0.8 },
    { pos: [-0.68, -25, -85], scale: 0.2, color: 0x4ade80, wireframe: false, opacity: 0.7 },
    { pos: [0.7, 25, -88], scale: 0.16, color: 0xfacc15, wireframe: true, opacity: 0.3 },
    { pos: [-0.8, 55, -100], scale: 0.25, color: 0x86efac, wireframe: true, opacity: 0.35 },
    { pos: [0.8, -45, -82], scale: 0.22, color: 0xf472b6, wireframe: false, opacity: 0.75 }
];

// World-space x for a leaf, based on how wide the view is at the leaf's depth
function leafBaseX(config) {
    const distance = camera.position.z - config.pos[2];
    const halfHeight = distance * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
    return config.pos[0] * halfHeight * camera.aspect;
}

// Load leaf model
gltfLoader.load(
    './assets/leaf/model.gltf',
    (gltf) => {
        leafModel = gltf.scene;

        // The model's geometry sits far from its origin, which pulls leaves toward the
        // center as they rotate. Re-center it so each leaf spins in place.
        const center = new THREE.Box3().setFromObject(leafModel).getCenter(new THREE.Vector3());
        leafModel.traverse((child) => {
            if (child.isMesh) {
                child.geometry.translate(-center.x, -center.y, -center.z);
            }
        });

        // Create leaf instances
        leafConfigs.forEach((config, index) => {
            // Check if this should be a point cloud leaf (index 4)
            if (index === 4) {
                // Create point cloud version (gaussian splat-like)
                const pointCloudGroup = new THREE.Group();

                // Extract vertices from the leaf model
                leafModel.traverse((child) => {
                    if (child.isMesh) {
                        const geometry = child.geometry;
                        const positions = geometry.attributes.position;

                        // Create point cloud geometry
                        const pointsGeometry = new THREE.BufferGeometry();
                        const pointsPositions = new Float32Array(positions.count * 3);

                        for (let i = 0; i < positions.count; i++) {
                            pointsPositions[i * 3] = positions.getX(i);
                            pointsPositions[i * 3 + 1] = positions.getY(i);
                            pointsPositions[i * 3 + 2] = positions.getZ(i);
                        }

                        pointsGeometry.setAttribute('position', new THREE.BufferAttribute(pointsPositions, 3));

                        // Create point cloud material
                        const pointsMaterial = new THREE.PointsMaterial({
                            color: config.color,
                            size: 0.15,
                            transparent: true,
                            opacity: config.opacity,
                            blending: THREE.AdditiveBlending,
                            sizeAttenuation: true
                        });

                        const points = new THREE.Points(pointsGeometry, pointsMaterial);
                        pointCloudGroup.add(points);
                    }
                });

                // Set position and scale
                pointCloudGroup.position.set(leafBaseX(config), config.pos[1], config.pos[2]);
                pointCloudGroup.scale.set(config.scale, config.scale, config.scale);

                // Random initial rotation
                pointCloudGroup.rotation.set(
                    Math.random() * Math.PI,
                    Math.random() * Math.PI,
                    Math.random() * Math.PI
                );

                scene.add(pointCloudGroup);
                shapes.push(pointCloudGroup);
            } else {
                // Regular mesh leaf
                const leaf = leafModel.clone();

                // Create material based on config
                const material = new THREE.MeshStandardMaterial({
                    color: config.color,
                    wireframe: config.wireframe,
                    transparent: true,
                    opacity: config.opacity,
                    roughness: 0.7,
                    metalness: 0.2,
                    side: THREE.DoubleSide,
                    emissive: config.color,
                    emissiveIntensity: config.wireframe ? 0.3 : 0.1
                });

                // Apply material to all meshes in the leaf
                leaf.traverse((child) => {
                    if (child.isMesh) {
                        child.material = material;
                    }
                });

                // Set position and scale
                leaf.position.set(leafBaseX(config), config.pos[1], config.pos[2]);
                leaf.scale.set(config.scale, config.scale, config.scale);

                // Random initial rotation
                leaf.rotation.set(
                    Math.random() * Math.PI,
                    Math.random() * Math.PI,
                    Math.random() * Math.PI
                );

                scene.add(leaf);
                shapes.push(leaf);

                // Animate wireframe/solid transition
                if (config.wireframe) {
                    animateWireframeTransition(leaf, material, index);
                }
            }
        });
    },
    undefined,
    (error) => {
        console.error('Error loading leaf model:', error);
    }
);

// Add lights for better leaf rendering
const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
scene.add(ambientLight);

const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
directionalLight1.position.set(10, 10, 5);
scene.add(directionalLight1);

const directionalLight2 = new THREE.DirectionalLight(0x4ade80, 0.4);
directionalLight2.position.set(-10, -10, -5);
scene.add(directionalLight2);

// Animate wireframe to solid transition
function animateWireframeTransition(leaf, material, index) {
    const tl = gsap.timeline({ repeat: -1, yoyo: true });
    const delay = index * 2;

    tl.to(material, {
        opacity: 0.6,
        emissiveIntensity: 0.5,
        duration: 3,
        delay: delay,
        ease: 'sine.inOut'
    })
    .to(material, {
        opacity: 0.3,
        emissiveIntensity: 0.2,
        duration: 3,
        ease: 'sine.inOut'
    });
}

// ===================================
// MOUSE INTERACTION
// ===================================

const mouse = {
    x: 0,
    y: 0
};

let mouseX = 0;
let mouseY = 0;

document.addEventListener('mousemove', (event) => {
    mouseX = (event.clientX / window.innerWidth) * 2 - 1;
    mouseY = -(event.clientY / window.innerHeight) * 2 + 1;

    mouse.x = event.clientX / window.innerWidth - 0.5;
    mouse.y = event.clientY / window.innerHeight - 0.5;
});

// ===================================
// SCROLL PROGRESS
// ===================================

window.addEventListener('scroll', () => {
    const scrollProgress = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
    document.querySelector('.scroll-progress').style.width = `${scrollProgress * 100}%`;

    // Rotate shapes on scroll
    shapes.forEach((shape, index) => {
        shape.rotation.x = scrollProgress * Math.PI * 2 * (index + 1);
        shape.rotation.y = scrollProgress * Math.PI * 2 * (index + 1);
    });
});

// ===================================
// ANIMATION LOOP
// ===================================

const clock = new THREE.Clock();

function animate() {
    requestAnimationFrame(animate);

    const elapsedTime = clock.getElapsedTime();

    // Animate green particles (gentle drift)
    particlesMesh.rotation.y = elapsedTime * 0.05;
    particlesMesh.rotation.x = elapsedTime * 0.02;

    // Animate pink particles (slower, like floating petals)
    pinkParticlesMesh.rotation.y = elapsedTime * 0.03;
    pinkParticlesMesh.rotation.x = elapsedTime * 0.015;
    pinkParticlesMesh.position.y = Math.sin(elapsedTime * 0.5) * 2;

    // Animate leaves with organic, spring-like movement
    shapes.forEach((leaf, index) => {
        if (!leaf) return;

        // Gentle rotation (like leaves turning in breeze)
        leaf.rotation.x += 0.0008 * (index + 1);
        leaf.rotation.y += 0.0015 * (index + 1);
        leaf.rotation.z += 0.0005 * (index + 1);

        // Floating animation (like growth and breathing)
        const baseY = leafConfigs[index]?.pos[1] || 0;
        const floatOffset = Math.sin(elapsedTime * 0.4 + index) * 1.5;
        leaf.position.y = baseY + floatOffset;

        // Gentle swaying
        const swayX = Math.sin(elapsedTime * 0.3 + index * 0.5) * 0.5;
        const swayZ = Math.cos(elapsedTime * 0.25 + index * 0.7) * 0.5;
        const baseX = leafConfigs[index] ? leafBaseX(leafConfigs[index]) : 0;
        const baseZ = leafConfigs[index]?.pos[2] || 0;
        leaf.position.x = baseX + swayX;
        leaf.position.z = baseZ + swayZ;

        // Subtle breathing/pulsing
        const basScale = leafConfigs[index]?.scale || 1;
        const pulse = 1 + Math.sin(elapsedTime * 0.2 + index) * 0.03;
        leaf.scale.set(basScale * pulse, basScale * pulse, basScale * pulse);
    });

    // Camera follows mouse (subtle parallax)
    camera.position.x += (mouseX * 5 - camera.position.x) * 0.05;
    camera.position.y += (-mouseY * 5 - camera.position.y) * 0.05;
    camera.lookAt(scene.position);

    renderer.render(scene, camera);
}

animate();

// ===================================
// WINDOW RESIZE
// ===================================

window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

// ===================================
// LOADING SCREEN
// ===================================

window.addEventListener('load', () => {
    const loading = document.getElementById('loading');
    gsap.to(loading, {
        opacity: 0,
        duration: 0.5,
        delay: 1,
        ease: 'power2.inOut',
        onComplete: () => {
            loading.style.display = 'none';
        }
    });
});

// ===================================
// SMOOTH SCROLL
// ===================================

document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
            target.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    });
});

// ===================================
// SCROLL ANIMATIONS WITH INTERSECTION OBSERVER
// ===================================

const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -100px 0px'
};

const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
        }
    });
}, observerOptions);

// Observe service cards
const serviceCards = document.querySelectorAll('.service-card');
serviceCards.forEach((card, index) => {
    card.style.opacity = '0';
    card.style.transform = 'translateY(50px)';
    observer.observe(card);

    card.addEventListener('transitionend', () => {
        if (card.classList.contains('is-visible')) {
            gsap.to(card, {
                y: 0,
                opacity: 1,
                duration: 0.8,
                delay: index * 0.1,
                ease: 'power2.out'
            });
        }
    }, { once: true });
});

// Observe work items
const workItems = document.querySelectorAll('.work-item');
workItems.forEach((item, index) => {
    item.style.opacity = '0';
    item.style.transform = 'translateY(50px)';
    observer.observe(item);

    item.addEventListener('transitionend', () => {
        if (item.classList.contains('is-visible')) {
            gsap.to(item, {
                y: 0,
                opacity: 1,
                duration: 0.8,
                delay: index * 0.15,
                ease: 'power2.out'
            });
        }
    }, { once: true });
});

// Observe about section
const aboutContent = document.querySelector('.about-content');
if (aboutContent) {
    aboutContent.style.opacity = '0';
    aboutContent.style.transform = 'translateX(-50px)';
    observer.observe(aboutContent);

    aboutContent.addEventListener('transitionend', () => {
        if (aboutContent.classList.contains('is-visible')) {
            gsap.to(aboutContent, {
                x: 0,
                opacity: 1,
                duration: 1,
                ease: 'power2.out'
            });
        }
    }, { once: true });
}

const aboutVisual = document.querySelector('.about-visual');
if (aboutVisual) {
    aboutVisual.style.opacity = '0';
    aboutVisual.style.transform = 'translateX(50px)';
    observer.observe(aboutVisual);

    aboutVisual.addEventListener('transitionend', () => {
        if (aboutVisual.classList.contains('is-visible')) {
            gsap.to(aboutVisual, {
                x: 0,
                opacity: 1,
                duration: 1,
                ease: 'power2.out'
            });
        }
    }, { once: true });
}

// Observe contact section
const contactContent = document.querySelector('.contact-content');
if (contactContent) {
    contactContent.style.opacity = '0';
    contactContent.style.transform = 'translateY(50px)';
    observer.observe(contactContent);

    contactContent.addEventListener('transitionend', () => {
        if (contactContent.classList.contains('is-visible')) {
            gsap.to(contactContent, {
                y: 0,
                opacity: 1,
                duration: 1,
                ease: 'power2.out'
            });
        }
    }, { once: true });
}

// ===================================
// BUTTON INTERACTIONS
// ===================================

const ctaButtons = document.querySelectorAll('.cta-button');
ctaButtons.forEach(button => {
    button.addEventListener('click', () => {
        // Scroll to film section on "Meet Masalo Dabbo" button
        if (button.textContent.includes('Meet Masalo Dabbo')) {
            document.querySelector('#work').scrollIntoView({
                behavior: 'smooth'
            });
        }

        // Scroll to contact section on "Get in Touch" button
        if (button.textContent.includes('Get in Touch')) {
            document.querySelector('#contact').scrollIntoView({
                behavior: 'smooth'
            });
        }
    });
});

// ===================================
// NAV ACTIVE STATE
// ===================================

const sections = document.querySelectorAll('section[id]');
const navLinks = document.querySelectorAll('.nav-link');

window.addEventListener('scroll', () => {
    let current = '';

    sections.forEach(section => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;
        if (window.scrollY >= sectionTop - 200) {
            current = section.getAttribute('id');
        }
    });

    navLinks.forEach(link => {
        link.classList.remove('active');
        if (link.getAttribute('href') === `#${current}`) {
            link.classList.add('active');
        }
    });
});
